import {
    Document,
    DocumentResult,
    Documents,
    ScoredMatches
} from '@amzn/coral_com-amazon-musicpaperrecordkeeperservice';

import { isNil } from 'lodash';
import { IndexMetadata } from 'src/components/Agreements';
import { DocumentRow } from 'src/components/Agreements/AgreementsTable/grid-types';
import { IndexEntry } from 'src/util/search-index';

function createNameEntries(document: Document): IndexEntry<IndexMetadata>[] {
    if (isNil(document.friendlyName)) {
        return [
            {
                item: {
                    key: document.documentId,
                    value: {
                        dataType: 'File Name',
                        colorClass: 'search-category-blue'
                    }
                },
                token: document.name
            }
        ];
    } else {
        return [
            {
                item: {
                    key: document.documentId,
                    value: {
                        dataType: 'File Name',
                        colorClass: 'search-category-blue'
                    }
                },
                token: document.friendlyName
            },
            {
                item: {
                    key: document.documentId,
                    value: {
                        dataType: 'Original File Name',
                        colorClass: 'search-category-rose'
                    }
                },
                token: document.name
            }
        ];
    }
}

export function createSearchIndexEntries(documents: Documents): IndexEntry<IndexMetadata>[] {
    return documents.flatMap((document) => [
        ...createNameEntries(document),
        ...(document.metadata['licensors']?.values?.map((licensor) => ({
            item: {
                key: document.documentId,
                value: {
                    dataType: 'Licensors',
                    colorClass: 'search-category-lavender'
                }
            },
            token: licensor
        })) ?? []),
        ...(document.metadata['services']?.values?.map((licensor) => ({
            item: {
                key: document.documentId,
                value: {
                    dataType: 'Services',
                    colorClass: 'search-category-lime'
                }
            },
            token: licensor
        })) ?? [])
    ]);
}

export function createDocumentRows(
    documents: Documents,
    licensors: Set<string> | undefined,
    searchResults: DocumentResult[] | undefined
): DocumentRow[] {
    const hasResults = !isNil(searchResults);
    const resultsMap = new Map(searchResults?.map((result) => [result.documentId, result]));

    function filterLicensors(document: Document): boolean {
        return isNil(licensors) || !isNil(document.metadata['licensors']?.values?.find((name) => licensors.has(name)));
    }

    function getMatches(documentId: string): ScoredMatches | undefined {
        return resultsMap.get(documentId)?.scoredMatches?.sort((a, b) => {
            return b.score - a.score;
        });
    }

    return documents
        .filter((document) => filterLicensors(document))
        .filter((document) => !hasResults || resultsMap.has(document.documentId))
        .map((document) => {
            const matches = getMatches(document.documentId);
            return {
                id: document.documentId,
                name: document.name,
                friendlyName: document.friendlyName,
                resolvedName: document.friendlyName ?? document.name,
                uploadedBy: document.uploadedBy,
                uploadedAt: new Date(Date.parse(document.uploadedAt + '+00:00')),
                description: document.description,
                services: document.metadata['services']?.values ?? [],
                licensors: document.metadata['licensors']?.values?.sort() ?? [],
                startDate: document.metadata['startDate']?.value,
                endDate: document.metadata['endDate']?.value,
                links: document.links ?? {},
                searchHits: matches,
                highestRelevance: matches?.[0]?.score ?? 0
            };
        })
        .sort((a, b) => {
            return (
                // sort order, highest to lowest precedence
                b.highestRelevance - a.highestRelevance ||
                compareStrings(a.licensors[0], b.licensors[0]) ||
                compareStrings(a.startDate ?? a.endDate, b.startDate ?? b.endDate)
            );
        });
}

function compareStrings(a: string | undefined, b: string | undefined) {
    if (isNil(a) && isNil(b)) {
        return 0;
    } else if (isNil(b)) {
        return -1;
    } else if (isNil(a)) {
        return 1;
    } else {
        return a.localeCompare(b);
    }
}
